import { useEffect, useState } from "react";
import { QuerySummary as HeadlessQuerySummary } from "@coveo/headless";
import { Facet as HeadlessFacet } from "@coveo/headless";
import { BreadcrumbManager as HeadlessBreadcrumbManager } from "@coveo/headless";
import {
	CoveoHeadlessTemplate,
	CoveoHeadlessFacetName
} from "components/coveo-headless/types/coveo-headless-types";
import { ResultList as HeadlessResultList, SearchEngine } from "@coveo/headless";
import SearchPageFilter from "components/search/client/SearchPageFilter";
import SearchPageResults from "components/search/client/SearchPageResults";
import BreadcrumbManager from "components/coveo-headless/components/BreadcrumbManager";

type SearchPageContentProps = {
	controller: HeadlessQuerySummary;
	facets: HeadlessFacet[];
	breadcrumbs: HeadlessBreadcrumbManager;
	page: string;
	resultList: HeadlessResultList;
	headlessEngine: SearchEngine;
	showMoreText: string;
	templates: CoveoHeadlessTemplate[];
	facetName: CoveoHeadlessFacetName[];
	uniqueId: string;
};

type SearchPageContentCTA = {
	text: string;
	link: string;
};

const SearchPageContent = ({
	controller,
	facets,
	breadcrumbs,
	page,
	resultList,
	headlessEngine,
	showMoreText,
	templates,
	facetName,
	uniqueId
}: SearchPageContentProps) => {
	const { noresults } = window[`coveoSearchData_${uniqueId}`];

	const [state, setState] = useState(controller.state);
	const [hideFilters, setHideFilters] = useState(false);
	useEffect(() => controller.subscribe(() => setState(controller.state)), [controller]);

	const { isLoading, hasResults } = state;

	if (!hasResults) {
		if (!isLoading) {
			return (
				<div
					className={`grid-center search-page-container ${
						!hasResults ? "search-page-no-results" : ""
					}`}
				>
					<div className="search-page-container-content-no-results">
						{noresults ? (
							<>
								<h3 className="h3-center-lightGray search-page-no-result-header">
									{noresults.text}
								</h3>
								<div className="search-page-none-facets">
									{noresults.cta.map((item: SearchPageContentCTA) => {
										let cleanLink = item.link.replace(/&amp;/g, "&");
										cleanLink = cleanLink.replace(/&percnt;/g, "%");
										const decodedLink = decodeURIComponent(cleanLink);
										let link = decodedLink.replace(/esg/, (m) => m.toUpperCase());
										link = link.replace(/(^\w|\s\w|\[\w)/g, (m) => m.toUpperCase());
										return (
											<a
												key={item.link}
												href={link}
												aria-label={item.text}
												className="search-page-no-result-suggestion-item"
											>
												{item.text}
											</a>
										);
									})}
								</div>
							</>
						) : null}
					</div>
				</div>
			);
		}
		return (
			<div className="grid-center search-page-container">
				<div className={`search-page-filters ${hideFilters ? "hide" : ""}`}>
					<div className="search-page-filters-title">
						<p className="label-4-left-almost-black">FILTERS</p>
					</div>
					<div
						aria-label="hide filter"
						className="search-page-filters-title-hide"
						role="button"
						tabIndex={0}
						onClick={() => {
							setHideFilters(!hideFilters);
						}}
						onKeyDown={() => {
							setHideFilters(!hideFilters);
						}}
					>
						<p className="label-4-left-bright-teal">HIDE FILTERS</p>
					</div>
					{facets.map((item, index) => {
						let title = "";
						facetName.map((name: CoveoHeadlessFacetName) => {
							if (item.state.facetId === name.facetField) {
								title = name.name;
								return title;
							}
							return title;
						});
						if (facetName[index].hide) {
							return null;
						}
						return (
							<SearchPageFilter
								key={facetName[index].facetField}
								controller={item}
								index={index}
								title={title}
							/>
						);
					})}
				</div>
				<div className="search-page-results"></div>
			</div>
		);
	}
	if (hasResults) {
		return (
			<div className={`${hideFilters ? "" : "grid-center"} search-page-container`}>
				<div className={`search-page-filters ${hideFilters ? "hide" : ""}`}>
					<div className="search-page-filters-title">
						<p className="label-4-left-almost-black">FILTERS</p>
					</div>
					<div
						aria-label="hide filter"
						className="search-page-filters-title-hide"
						role="button"
						tabIndex={0}
						onClick={() => {
							setHideFilters(!hideFilters);
						}}
						onKeyDown={() => {
							setHideFilters(!hideFilters);
						}}
					>
						<p className="label-4-left-bright-teal">HIDE FILTERS</p>
					</div>
					{facets.map((item, index) => {
						let title = "";
						facetName.map((name: CoveoHeadlessFacetName) => {
							if (item.state.facetId === name.facetField) {
								title = name.name;
								return title;
							}
							return title;
						});
						if (facetName[index].hide) {
							return null;
						}
						return (
							<SearchPageFilter
								key={facetName[index].facetField}
								controller={item}
								index={index}
								title={title}
							/>
						);
					})}
				</div>
				<div
					className={`search-page-results ${
						hideFilters ? "search-page-results-hidden-filters" : ""
					}`}
				>
					{hideFilters && (
						<div
							className="search-page-filters-close"
							role="button"
							aria-label="hide filter"
							tabIndex={0}
							onClick={() => {
								setHideFilters(false);
							}}
							onKeyDown={() => {
								setHideFilters(false);
							}}
						>
							Show Filters
						</div>
					)}
					{isLoading ? null : (
						<>
							<BreadcrumbManager controller={breadcrumbs} page={page} />
							<SearchPageResults
								controller={resultList}
								headlessEngine={headlessEngine}
								showMoreText={showMoreText}
								templates={templates}
							/>
						</>
					)}
				</div>
			</div>
		);
	}
	return null;
};

export default SearchPageContent;
